<script setup lang="ts">
/**
 * The landing's top bar, the harbour HUD's own (`ui/hud/HarbourTopBar.vue`):
 * the kit's `.k-bar` steel strip, the wordmark, the sections as round brass
 * `.k-knob`s (icons alone on a phone, capsules with names from 1100 px, the
 * current one ringed in gold), the sound knob and the yellow PLAY plate. It is
 * a fixed bar: the page's scroller sits under it, and the hero leaves room for it.
 */
import { computed } from 'vue';

import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';
import ActionButton from '@/ui/panel/ActionButton.vue';

import { NAV } from '../landing-content';
import { landingSound } from '../landing-sound';
import LandingWordmark from './LandingWordmark.vue';
import SoundIcon from './SoundIcon.vue';

defineProps<{ active: string }>();
defineEmits<{ play: []; goto: [id: string]; top: [] }>();
const { t } = useI18n();
const soundLabel = computed(() => t(landingSound.enabled.value ? 'landing.sound.mute' : 'landing.sound.unmute'));
</script>

<template>
  <header class="bar k-bar">
    <button type="button" class="home" :aria-label="t('landing.nav.home')" @click="$emit('top')">
      <LandingWordmark class="home-mark" />
    </button>

    <nav class="tabs" :aria-label="t('landing.nav.sections')">
      <button
        v-for="item in NAV"
        :key="item.id"
        type="button"
        class="k-knob tab"
        :class="{ on: active === item.id }"
        :aria-label="t(item.labelKey)"
        :aria-current="active === item.id ? 'true' : undefined"
        @click="$emit('goto', item.id)"
      >
        <GameIcon :name="item.icon" :size="32" />
        <span class="k-label tab-name">{{ t(item.labelKey) }}</span>
      </button>
    </nav>

    <div class="actions">
      <button type="button" class="k-knob" :aria-label="soundLabel" :aria-pressed="landingSound.enabled.value" @click="landingSound.toggle()">
        <SoundIcon :muted="!landingSound.enabled.value" />
      </button>
      <ActionButton variant="primary" size="sm" @click="$emit('play')">{{ t('landing.hero.play') }}</ActionButton>
    </div>
  </header>
</template>

<style scoped>
/* The strip's paint is `.k-bar`; this places it and clears the knobs' 8 px shadow. */
.bar {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: clamp(6px, 1.6vw, 22px);
  padding: calc(6px + env(safe-area-inset-top, 0px)) calc(10px + env(safe-area-inset-right, 0px)) 8px calc(10px + env(safe-area-inset-left, 0px));
}

.home {
  display: flex;
  align-items: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: none;
  touch-action: manipulation;
}

/* The name is dropped on a phone: the anchor alone is the way home. */
.home-mark :deep(.word) {
  display: none;
}

/* Icon knobs: a scrolling strip so a 320 px phone still reaches every section. */
.tabs {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 14px;
  overflow-x: auto;
  /* Room for the knobs' rim, shadow and gold glow, which the scroller would clip. */
  padding: 8px 12px 14px;
  margin: -8px 0 -14px;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  flex: none;
}

.tab-name {
  display: none;
  font-size: 0.85rem;
}

.actions {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: none;
}

@media (min-width: 560px) {
  .home-mark :deep(.word) {
    display: inline;
  }
}

@media (min-width: 1100px) {
  .tab {
    padding: 0 16px 0 8px;
  }

  .tab-name {
    display: inline;
  }
}

@media (max-height: 520px) {
  .bar {
    padding-top: calc(3px + env(safe-area-inset-top, 0px));
    padding-bottom: 6px;
  }
}
</style>
